import dayjs from 'dayjs';

import { DATE_KEY_FORMAT } from '@/helpers';
import { TeamMemberStatusDTO } from '@/modules/common/types/team-member-status-dto';

import { applyFilterCascades } from '../helpers/filter-cascades';
import { logPlannerNavigation } from '../helpers/log';
import { computeFilterReloadViewport } from '../helpers/viewport';
import { clearPersistedFilters } from '../persistence/clear-persisted-filters';
import { FilterCascadeNotice, FiltersSlice, PlannerFilters, PlannerSliceCreator } from '../types';

// Filter key for the planner's member-status filter (see planner-filters-config.tsx).
export const PLANNER_MEMBER_STATUS_FILTER_KEY = 'memberStatus';

// Default planner filters applied on the very first visit (no URL/localStorage state):
// filter members by status to all statuses except Placeholder and Archived. Mirrors the
// team page default (DEFAULT_TEAM_STATUS_FILTER) but uses the planner's DTO filter values.
export const DEFAULT_PLANNER_FILTERS: PlannerFilters = {
  [PLANNER_MEMBER_STATUS_FILTER_KEY]: [
    TeamMemberStatusDTO.ACTIVE,
    TeamMemberStatusDTO.BASIC,
    TeamMemberStatusDTO.INVITED,
    TeamMemberStatusDTO.INVITE_EXPIRED,
  ],
};

const buildCascadeNotice = (
  previous: FilterCascadeNotice | null,
  kind: FilterCascadeNotice['kind']
): FilterCascadeNotice => ({ kind, id: (previous?.id ?? 0) + 1 });

/**
 * Filter values + the cascade rules (helpers/filter-cascades.ts). A filter change
 * additionally re-bootstraps the active group's query range around the visible
 * viewport and queues a pixel-exact scroll restore — see computeFilterReloadViewport.
 * Toasts for cascade clears are NOT shown here: the slice sets filterCascadeNotice
 * and the toast effect (store/effects) renders them (stores don't speak i18n).
 */
export const createFiltersSlice: PlannerSliceCreator<FiltersSlice> = (set, get) => ({
  filters: DEFAULT_PLANNER_FILTERS,
  filterCascadeNotice: null,

  setFilters: (key: string, value: string | string[]) =>
    set(state => {
      // Skip if value hasn't changed
      if (JSON.stringify(state.filters[key]) === JSON.stringify(value)) {
        return state;
      }

      // Cascade clears happen inside this single set() so all clears produce
      // ONE filters change → one refetch (invariant: one set() per user intent).
      const cascade = applyFilterCascades(state.filters, key, value);

      const firstVisibleDate = state.firstVisibleDate || dayjs();
      const activeGroupBy = state.groupBy;
      const { bufferedPeriod, restoreScrollLeft, isStaleZeroSnapshot, fallbackScrollTarget } =
        computeFilterReloadViewport(state.dates, state.visibleDateRange, firstVisibleDate);

      logPlannerNavigation('setFilters', {
        key,
        activeGroup: activeGroupBy,
        visibleBufferedRange: `${bufferedPeriod.startDate}..${bufferedPeriod.endDate}`,
        restoreScrollLeft,
        ...(isStaleZeroSnapshot && {
          staleZeroFallbackTarget: fallbackScrollTarget.format(DATE_KEY_FORMAT),
        }),
      });

      return {
        filters: cascade.nextFilters,
        ...(cascade.clearedAvailability && {
          filterCascadeNotice: buildCascadeNotice(state.filterCascadeNotice, 'availability'),
        }),
        ...(cascade.clearedProbability && {
          filterCascadeNotice: buildCascadeNotice(state.filterCascadeNotice, 'probability'),
        }),
        queryDateRanges: {
          ...state.queryDateRanges,
          [activeGroupBy]: bufferedPeriod,
        },
        pendingScrollRestoreLeft: isStaleZeroSnapshot ? null : restoreScrollLeft,
        ...(isStaleZeroSnapshot && { pendingScrollTarget: fallbackScrollTarget }),
      };
    }),

  resetFilter: () => {
    const state = get();
    const filterKeys = Object.keys(state.filters);

    // Eagerly clear the persisted values (localStorage + URL) to prevent a
    // refresh in the persist-write race window from re-hydrating stale filters.
    clearPersistedFilters(filterKeys);

    if (filterKeys.length === 0) {
      set({ filters: {} });
      return;
    }

    const firstVisibleDate = state.firstVisibleDate || dayjs();
    const activeGroupBy = state.groupBy;
    const { bufferedPeriod, restoreScrollLeft, isStaleZeroSnapshot, fallbackScrollTarget } =
      computeFilterReloadViewport(state.dates, state.visibleDateRange, firstVisibleDate);

    logPlannerNavigation('resetFilter', {
      activeGroup: activeGroupBy,
      visibleBufferedRange: `${bufferedPeriod.startDate}..${bufferedPeriod.endDate}`,
      restoreScrollLeft,
      ...(isStaleZeroSnapshot && {
        staleZeroFallbackTarget: fallbackScrollTarget.format(DATE_KEY_FORMAT),
      }),
    });

    set({
      filters: {},
      queryDateRanges: {
        ...state.queryDateRanges,
        [activeGroupBy]: bufferedPeriod,
      },
      pendingScrollRestoreLeft: isStaleZeroSnapshot ? null : restoreScrollLeft,
      ...(isStaleZeroSnapshot && { pendingScrollTarget: fallbackScrollTarget }),
    });
  },
});
